import { CircleDot, Trophy, Crown, User, Users, Medal, ArrowRight } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { scrollToId } from "../lib/scroll";

const PACKAGES = [
  {
    icon: CircleDot,
    name: "Mini Game",
    desc: "20–30 edited photos",
    price: "$50",
    testid: "package-mini-game",
  },
  {
    icon: Trophy,
    name: "Game Day",
    desc: "50–75 edited photos",
    price: "$85",
    testid: "package-game-day",
  },
  {
    icon: Crown,
    name: "Full Game",
    desc: "100+ edited photos",
    price: "$125",
    featured: true,
    testid: "package-full-game",
  },
  {
    icon: User,
    name: "Player Package",
    desc: "75+ action shots + 5–10 portraits",
    price: "$100",
    testid: "package-player",
  },
  {
    icon: Users,
    name: "Team Package",
    desc: "Full team coverage + individual shots",
    price: "$200–$300",
    testid: "package-team",
  },
  {
    icon: Medal,
    name: "Tournament Package",
    desc: "Multiple games / extended coverage",
    price: "$350–$500+",
    testid: "package-tournament",
  },
];

function Card({ pkg, index }) {
  const Icon = pkg.icon;
  const inner = (
    <div
      data-testid={pkg.testid}
      className={`group relative flex h-full flex-col gap-6 p-8 transition-colors duration-300 md:p-10 ${
        pkg.featured
          ? "bg-ink"
          : "border border-white/5 bg-surface hover:border-accent/50"
      }`}
    >
      <div className="flex items-start justify-between">
        <span
          className={`flex h-14 w-14 items-center justify-center rounded-2xl ${
            pkg.featured
              ? "bg-gradient-to-br from-accent to-accentlight text-ink"
              : "bg-surface-hover text-accentlight"
          } transition-transform duration-300 group-hover:-rotate-6 group-hover:scale-105`}
        >
          <Icon className="h-7 w-7" strokeWidth={1.75} />
        </span>
        {pkg.featured && (
          <span className="rounded-full bg-gradient-to-r from-accent to-accentlight px-3 py-1 text-[10px] font-extrabold uppercase tracking-[0.2em] text-ink">
            Best value
          </span>
        )}
      </div>

      <div>
        <h3 className="font-display text-3xl tracking-wider md:text-4xl">{pkg.name}</h3>
        <p className="mt-2 text-sm leading-relaxed text-white/60">{pkg.desc}</p>
      </div>

      <p
        className={`mt-auto font-display text-6xl leading-none md:text-7xl ${
          pkg.featured
            ? "bg-gradient-to-r from-accent to-accentlight bg-clip-text text-transparent"
            : "text-accentlight"
        }`}
      >
        {pkg.price}
      </p>
    </div>
  );

  if (pkg.featured) {
    return (
      <Reveal delay={(index % 3) * 0.08} className="h-full">
        <div className="h-full rounded-xl bg-gradient-to-b from-accent via-accentlight/40 to-accent p-[1.5px] shadow-[0_30px_80px_-30px_rgba(30,136,229,0.5)]">
          <div className="h-full rounded-[10px]">{inner}</div>
        </div>
      </Reveal>
    );
  }

  return (
    <Reveal delay={(index % 3) * 0.08} className="h-full">
      <div className="h-full rounded-xl">{inner}</div>
    </Reveal>
  );
}

export default function Packages() {
  return (
    <section id="packages" className="relative bg-surface/40 py-24 md:py-36">
      <div className="mx-auto w-full max-w-7xl px-6 md:px-10">
        <div className="mb-14 flex flex-col gap-6 md:mb-20 md:flex-row md:items-end md:justify-between">
          <SectionHeading
            eyebrow="Packages & Pricing"
            title={
              <>
                PICK YOUR
                <br />
                <span className="bg-gradient-to-r from-accent to-accentlight bg-clip-text text-transparent">
                  COVERAGE
                </span>
              </>
            }
          />
          <Reveal delay={0.15}>
            <p className="max-w-sm text-sm leading-relaxed text-white/60">
              Every package gets fully edited, gallery-delivered photos. Not sure
              which fits? Book any package and we&rsquo;ll sort the details together.
            </p>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-8 lg:grid-cols-3">
          {PACKAGES.map((pkg, i) => (
            <Card key={pkg.name} pkg={pkg} index={i} />
          ))}
        </div>

        <Reveal delay={0.1} className="mt-14 flex justify-center">
          <a
            href="#booking"
            data-testid="packages-book-button"
            onClick={(e) => {
              e.preventDefault();
              scrollToId("#booking");
            }}
            className="group inline-flex items-center gap-3 rounded-full border border-accentlight/40 px-8 py-4 text-sm font-bold uppercase tracking-wider text-accentlight transition-all duration-300 hover:gap-5 hover:border-accentlight hover:bg-accentlight/10"
          >
            Book your package
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </a>
        </Reveal>
      </div>
    </section>
  );
}
